import { isErrorWithCode } from '@gonfalon/errors/isErrorWithCode';
import { isErrorWithMessage } from '@gonfalon/errors/isErrorWithMessage';
import { NetworkError } from '@gonfalon/errors/NetworkError';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import type { FetchResponse } from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import { getAPIClientConfig } from '../config';

function hasComponentStack(error: unknown): error is { componentStack: string } {
  return (
    error !== undefined &&
    error !== null &&
    typeof error === 'object' &&
    'componentStack' in error &&
    typeof error.componentStack === 'string' &&
    error.componentStack !== ''
  );
}

export async function reactQueryResponseAdapter<
  T extends Record<string | number, unknown>,
  Options,
  Media extends MediaType,
>(promise: Promise<FetchResponse<T, Options, Media>>): Promise<FetchResponse<T, Options, Media>['data'] & {}> {
  const config = getAPIClientConfig();

  let fetchResponse: FetchResponse<T, Options, Media>;
  try {
    fetchResponse = await promise;
  } catch (error) {
    const networkError = new NetworkError({
      message: isErrorWithMessage(error) ? error.message : 'Unknown network error',
      cause: error,
      stack: hasComponentStack(error) ? error.componentStack : undefined,
    });
    if (config.onError) {
      config.onError(networkError);
    }
    throw networkError;
  }

  const { data, error, response } = fetchResponse;

  if (response.ok) {
    // Casting because `undefined` is a valid return type, but lacks wrapping
    // which makes queryFn unhappy
    return data ?? ({} as FetchResponse<T, Options, Media>['data'] & {});
  }

  let code = 'unknown_error';
  if (isErrorWithCode(error)) {
    const errorWithCode: {
      code: string;
    } = error;
    code = errorWithCode.code;
  }
  let message = response.statusText || `HTTP ${response.status}`;
  if (isErrorWithMessage(error)) {
    const errorWithMessage: {
      message: string;
    } = error;
    message = errorWithMessage.message;
  }
  let stack: string | undefined;
  if (hasComponentStack(error)) {
    const errorWithComponentStack: {
      componentStack: string;
    } = error;
    stack = errorWithComponentStack.componentStack;
  }

  const clientError = new RESTAPIError({
    status: response.status,
    url: response.url,
    code,
    message,
    cause: error,
    stack,
  });

  if (config.onError) {
    config.onError(clientError);
  }

  throw clientError;
}
